import type { ReactNode } from 'react';
import { css, keyframes, Theme } from '@emotion/react';
import { lighten, transparentize } from 'polished';
import {
  Root,
  Trigger,
  Portal,
  Content,
  type TooltipProps as RootProps,
  type TooltipContentProps as ContentProps,
} from '@radix-ui/react-tooltip';

const DefaultSideAlign: Record<NonNullable<ContentProps['side']>, ContentProps['align']> = {
  top: 'start',
  bottom: 'end',
  left: 'start',
  right: 'end',
};

type CombinedProps = RootProps & Pick<ContentProps, 'side' | 'align'>;

export interface TooltipProps extends CombinedProps {
  title: string | JSX.Element | null;
  hideArrow?: boolean;
  children: ReactNode;
}

export const Tooltip = ({ title, side = 'left', align, hideArrow, children, ...other }: TooltipProps) => {
  if (!title) return <>{children}</>;

  return (
    <Root delayDuration={100} {...other}>
      <Trigger asChild>{children}</Trigger>
      <Portal>
        <Content side={side} align={align || DefaultSideAlign[side]} sideOffset={8} css={[tooltipStyle, hideArrow && hideArrowStyle]}>
          {title}
        </Content>
      </Portal>
    </Root>
  );
};

const tooltipAnimation = keyframes`
  0% {
    --mask-size: 0%;
    --mask-color: rgba(255, 255, 255, 0);
  }
  100% {
    --mask-size: 100%;
    --mask-color: rgba(255, 255, 255, 1);
  }
`;

const tooltipStyle = (theme: Theme) => css`
  --background-color: ${transparentize(0.75, lighten(0.1, theme.color.secondary[100]))};
  --angle: 0deg;
  --mask-position: circle at center;
  --mask-size: 100%;
  --mask-color: rgba(255, 255, 255, 1);

  @property --mask-size {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 100%;
  }

  @property --mask-color {
    syntax: '<color>';
    inherits: false;
    initial-value: rgba(255, 255, 255, 1);
  }

  display: flex;
  align-items: center;
  padding: 2px 12px 0 12px;
  min-height: 28px;
  border-radius: 28px;
  font-size: 0.875rem;
  color: ${transparentize(0.1, theme.color.foreground)};
  background-image: linear-gradient(var(--angle), var(--background-color) calc(100% - 22px), ${theme.color.secondary[80]});
  user-select: none;
  pointer-events: none;

  backdrop-filter: brightness(50%) blur(8px);

  animation-duration: 200ms;
  animation-timing-function: ease-in-out;

  will-change: transform, opacity, background-image;

  mask-image: radial-gradient(var(--mask-position), var(--mask-color) var(--mask-size), transparent);

  &[data-side='left'] {
    --angle: 135deg;
    --mask-position: circle at bottom right;
    border-bottom-right-radius: 0;
  }

  &[data-side='right'] {
    --angle: -45deg;
    --mask-position: circle at top left;
    border-top-left-radius: 0;
  }

  &[data-side='top'] {
    --angle: -135deg;
    --mask-position: circle at bottom left;
    border-bottom-left-radius: 0;
    margin-left: calc(var(--radix-popper-anchor-width) * 0.5);
  }

  &[data-side='bottom'] {
    --angle: 45deg;
    --mask-position: circle at top right;
    border-top-right-radius: 0;
    margin-right: calc(var(--radix-popper-anchor-width) * 0.5);
  }

  &[data-state='delayed-open'] {
    animation-name: ${tooltipAnimation};
  }
`;

const hideArrowStyle = css`
  border-radius: 28px !important;
  background-image: none;
  background-color: var(--background-color);
`;
